import React, { useEffect, useState } from 'react';
import { Recipe, User } from '../types';
import { buildWhatsAppUrl, copyTextToClipboard, createRecipeShareLink } from '../services/storageService';

interface ShareRecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: Recipe | null;
  currentUser: User | null;
}

const WHATSAPP_ICON_PATH = "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.888 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z";

export const ShareRecipeModal: React.FC<ShareRecipeModalProps> = ({ isOpen, onClose, recipe, currentUser }) => {
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // De link wordt direct gemaakt zodra het venster opent (de Firestore-kopie met foto
  // wordt op de achtergrond opgeslagen). De deelknoppen zijn daarna een nieuwe tik,
  // zodat de browser WhatsApp / het deelmenu niet blokkeert.
  useEffect(() => {
    if (!isOpen || !recipe) return;
    setCopied(false);
    setShareUrl(createRecipeShareLink(currentUser, recipe));
  }, [isOpen, recipe?.id]);

  if (!isOpen || !recipe) return null;

  const message = shareUrl
    ? `🍽️ Bekijk dit recept: *${recipe.title}*\n\nOpen het in superMarct:\n${shareUrl}`
    : '';

  const handleWhatsApp = () => {
    if (!shareUrl) return;
    const url = buildWhatsAppUrl(message);
    const win = window.open(url, '_blank');
    if (!win) window.location.href = url;
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.share({
        title: `Recept: ${recipe.title}`,
        text: `🍽️ Bekijk dit recept: ${recipe.title}`,
        url: shareUrl
      });
    } catch (e: any) {
      if (e?.name !== 'AbortError') console.warn('Native share failed', e);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    if (await copyTextToClipboard(shareUrl)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="bg-white dark:bg-[#15241b] w-full max-w-sm rounded-[28px] p-6 shadow-2xl relative z-10 animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-white/10 max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined">share</span>
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Recept delen</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{recipe.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Link */}
        <div className="mb-5">
          <label className="text-[11px] font-bold text-gray-500 uppercase ml-1 mb-1 block">Deellink</label>
          {shareUrl ? (
            <input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full bg-gray-50 dark:bg-black/20 rounded-xl px-3.5 py-3 text-xs font-medium border border-gray-200 dark:border-white/10 outline-none text-gray-600 dark:text-gray-300"
            />
          ) : (
            <div className="w-full bg-gray-50 dark:bg-black/20 rounded-xl px-3.5 py-3 text-xs border border-gray-200 dark:border-white/10 text-gray-400 flex items-center gap-2">
              <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              Link aanmaken...
            </div>
          )}
        </div>

        <div className="space-y-2.5">
          <button
            onClick={handleWhatsApp}
            disabled={!shareUrl}
            className="w-full py-3.5 rounded-2xl bg-[#25D366] hover:bg-[#22c35e] text-white font-bold text-sm shadow-lg shadow-green-500/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d={WHATSAPP_ICON_PATH} /></svg>
            <span>Deel via WhatsApp</span>
          </button>

          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <button
              onClick={handleNativeShare}
              disabled={!shareUrl}
              className="w-full py-3 rounded-2xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-xs active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-[18px]">ios_share</span>
              <span>Delen via...</span>
            </button>
          )}

          <button
            onClick={handleCopy}
            disabled={!shareUrl}
            className="w-full py-3 rounded-2xl bg-surface-light dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 font-bold text-xs hover:bg-gray-100 dark:hover:bg-white/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-[18px]">{copied ? 'check_circle' : 'content_copy'}</span>
            <span>{copied ? 'Link gekopieerd!' : 'Kopieer link'}</span>
          </button>

          <button
            onClick={onClose}
            className="w-full py-2.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 font-medium transition text-center"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};
